import React, { useEffect, useState } from "react";
import { Bounce, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useAuth } from "../../../../context/AuthContext";
import classes from "./AddEquipmentManagement.module.css";
import CommonFooter from "../../../CommonFooter/CommonFooter";
import { env } from "../../../../constants/env";
import QuestionSection from "../../../ui/QuestionSection/QuestionSection";
import { useCertification } from "../../../../hooks/useCertification";

const questionSets = [
  {
    render: (onAnswerChange, answers, editable) => (
      <>
        <QuestionSection
          number="5.1"
          title="Procedure and/or Process for Management of Laboratory Equipment"
          questions={[
            {
              content:
                "Has the laboratory defined a procedure and/or process that addresses, but is not limited to, the following?",
              hideOptions: true,
            },
            {
              content: "Determining the need and specification of equipment;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Selection of equipment;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Procurement of equipment;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Acceptance and installation;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Creation and maintenance of equipment records (including the equipment service schedule);",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Unique labelling of equipment (serial number, asset number, date of calibration, etc.);",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Defining the equipment maintenance and service frequency;",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Management of defective equipment (including decontamination);",
              number: "i",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Management of obsolete equipment;",
              number: "j",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Management of safe handling, transportation, storage and use to avoid deterioration and contamination;",
              number: "k",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Tracking and verification of completion of repairs and services.",
              number: "l",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.2"
          title="Access to Required Equipment"
          questions={[
            {
              content:
                "Does the laboratory have access to the required equipment for the performance of laboratory activities?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.3"
          title="Adherence to Proper Equipment Protocol"
          questions={[
            {
              content: (
                <>
                  Is equipment installed and placed as specified in the
                  operator’s manuals and uniquely labelled or marked?
                  <br />
                  Note: Equipment should be properly placed as specified in the
                  user manual away from potential hazards including but not
                  limited to the following: water, direct sunlight, vibrations,
                  traffic
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.4"
          title="Training, Competency and Authorization of Equipment Users"
          questions={[
            {
              content: (
                <>
                  Is all equipment operated by trained, competent and authorized
                  personnel?
                  <br />
                  Note: Records of training, competency and authorization shall
                  be available
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.5"
          title="Procedure and/or Process for Validation and Verification of Equipment"
          questions={[
            {
              content:
                "Has the laboratory defined a procedure and/or process that addresses, but is not limited to, the following?",
              hideOptions: true,
            },
            {
              content:
                "Defining the validation or verification protocol (including the authorization for the intended use);",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Performing equipment verification or validation;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Defining verification or validation report",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
        <QuestionSection
          number="5.6"
          title="Equipment Verification and Documentation"
          questions={[
            {
              content:
                "Is all equipment verified onsite upon installation after maintenance and repair before use?",
              hideOptions: true,
            },
            {
              content:
                "Are specific verification protocols in place for each item of equipment?",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Has validation information been obtained from the manufacturer as part of the verification?",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Have performance characteristics been appropriately selected and evaluated as per intended use?",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Were the verification studies appropriate and adequate?",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Was the analysis of data appropriate for the selected performance characteristics?",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Have the verification results and reports been reviewed and approved by an authorized person?",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.7"
          title="Equipment Records"
          questions={[
            {
              content:
                "Is current equipment inventory data available for all equipment in the laboratory?",
              hideOptions: true,
            },
            {
              content:
                "Manufacturer and supplier details, and sufficient information to uniquely identify each item of equipment, including software and firmware;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Dates of receipt, acceptance testing and entry into service;",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Evidence of verification or validation that equipment conforms with specified acceptability criteria;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Current location of equipment;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Condition when received (e.g., new, used, or reconditioned);",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Manufacturer's instructions;",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Program for preventive maintenance;",
              number: "g",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Maintenance activities performed by the laboratory or approved external service provider;",
              number: "h",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Damage to, malfunction, modification, or repair of the equipment;",
              number: "i",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Equipment performance records, such as reports or certificates of calibrations or verifications, or both, including dates, times, and results;",
              number: "j",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Date of last service;",
              number: "k",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Date of next service",
              number: "l",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.8"
          title="Defective Equipment Waiting for Repair"
          questions={[
            {
              content: (
                <>
                  Is defective equipment waiting for repair not used and clearly
                  labelled?
                  <br />
                  Note 1 Labels should include the date of malfunction and ‘not
                  in use’ and signature of approval
                  <br />
                  Note 2: All equipment malfunctions must be investigated and
                  documented as per the non-conforming procedure. If the user
                  cannot resolve the problem, a repair order must be initiated
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.9"
          title="Procedure and/or Process for Calibration of Equipment"
          questions={[
            {
              content:
                "Has the laboratory defined a procedure and/or process that addresses, but is not limited to, the following?",
              hideOptions: true,
            },
            {
              content: "Frequency of calibration;",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Handling of in-house calibrations (pipettes, thermometers, timers, etc.);",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Management of calibrations performed by external service providers;",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Recording of metrological traceability;",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: "Handling of failed calibrations;",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Retention of calibration records (use of stickers and calibration certificates)",
              number: "f",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.10"
          title="Obsolete Equipment"
          questions={[
            {
              content:
                "Is obsolete equipment appropriately labelled and removed from the laboratory or path of workflow?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.11"
          title="Equipment Calibration and Metrological Traceability"
          questions={[
            {
              content:
                "Note: Documentation of calibration traceability to a higher order reference material or reference procedure may be provided by an examination system manufacturer. Such documentation is acceptable if the manufacturer's examination system and calibration procedures are used without modification",
              hideOptions: true,
            },
            {
              content:
                "Is routine calibration of laboratory measuring equipment (including pipettes, centrifuges, balances, and thermometers) scheduled, at minimum following manufacturer’s recommendations?",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "When routine calibration of laboratory measuring equipment (including pipettes, centrifuges, balances, and thermometers) is performed offsite (externally), are there records of verification before use?",
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Is information on metrological traceability (e.g., use of reference materials and equipment like certified thermometers, tachometer) available?",
              number: "c",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Is there evidence of review of calibrations records (e.g., calibration certificates, calibration reports, etc.) by the laboratory before acceptance back into use?",
              number: "d",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content:
                "Where it is not possible to provide traceability using an accredited calibration laboratory, are certified reference materials, examination and calibration by another procedure, use of mutual consent standards or methods used for in house calibrations?",
              number: "e",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />

        <QuestionSection
          number="5.12"
          title="Equipment Preventive Maintenance"
          questions={[
            {
              content: (
                <>
                  Is routine user preventive maintenance performed on all
                  equipment and recorded according to manufacturer’s minimum
                  requirements?
                  <br />
                  Note: Preventative maintenance by operators must be done on
                  all equipment used in examinations including centrifuges,
                  autoclaves, microscopes, and safety cabinets
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
        <QuestionSection
          number="5.13"
          title="Equipment Service Maintenance"
          questions={[
            {
              content: (
                <>
                  Is equipment routinely serviced according to a schedule as per
                  the minimum manufacturer’s recommendations by approved
                  internal or external service providers and is this information
                  documented in appropriate logs?
                  <br />
                  Note: All equipment must be serviced at specified intervals by
                  a qualified service engineer either through service contracts
                  or otherwise. Service schedules must at minimum meet
                  manufacturer’s requirements
                </>
              ),
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
        <QuestionSection
          number="5.14"
          title="Equipment Adverse Incident Reporting."
          questions={[
            {
              content:
                "Are there records of investigation, identification and implementation of corrective actions taken and follow-up?",
              number: "a",
              multiline: true,
              placeholder: "Add comment",
            },
            {
              content: (
                <>
                  Is there documentation of reports made to manufacturers or
                  suppliers and appropriate authorities of adverse incidents and
                  accidents where applicable?
                  <br />
                  Are the manufacturer’s operator manuals readily available to
                  testing personnel and available in the language understood by
                  personnel?
                </>
              ),
              number: "b",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
        <QuestionSection
          number="5.15"
          title="Manufacturer’s Operator Manual"
          questions={[
            {
              content:
                "Are the manufacturer’s operator manuals readily available to testing personnel and available in the language understood by personnel?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
        <QuestionSection
          number="5.16"
          title="Use of Equipment"
          questions={[
            {
              content:
                "Are there precautions (e.g., password protection) in place to prevent unintended adjustments of automated equipment, where applicable?",
              multiline: true,
              placeholder: "Add comment",
            },
          ]}
          onAnswerChange={onAnswerChange}
          answers={answers}
          disabled={!editable}
        />
      </>
    ),
  },
];

const AddEquipmentManagement = () => {
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);

  const { labId, questionnaireId } = useParams(); // ✅ get questionnaireId too

  const editable = questionnaireId ? status === "Not Attached" : true;

  const {
    formData: answers,
    handleChange: handleAnswerChange,
    handleDone,
    editMode,
    loading,
    isFetching,
  } = useCertification(
    "Equipment Management", // section
    {}, // initialAnswers
    questionnaireId // questionnaireId (optional)
  );

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  return (
    <div
      className={`${classes.wrapper} ${!editMode ? classes.fromParams : ""}`}
    >
      <div
        className="d-flex align-items-center gap-2 mb-4"
        onClick={() => navigate(-1)}
        role="button"
      >
        <IoIosArrowRoundBack size={35} />
        <h2 className="m-0">
          Section 05: Equipment Management {questionnaireId && "(Editing)"}
        </h2>
      </div>

      {isFetching || loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {questionSets[currentSet].render(
            handleAnswerChange,
            answers,
            editMode
          )}

          <CommonFooter
            handlePrevious={handlePrevious}
            handleCancel={() => navigate(-1)}
            handleNext={handleNext}
            handleDone={handleDone}
            questionSets={questionSets}
            currentSet={currentSet}
            cancelText="Cancel"
            doneText={
              questionnaireId ? "Update Assessment" : "Submit Assessment"
            }
          />
        </>
      )}
    </div>
  );
};

export default AddEquipmentManagement;
